/**
 * FrameStore singleton
 *
 * This class is ment to store all the data that is needed for the react three fiber useFrame hooks
 */
import AudioManager from '@repo/shared/AudioManager'
import {Vector3} from 'three'
import {clamp, inverseLerp} from 'three/src/math/MathUtils'
import {IInfoMessage, ILeaderboardMessage} from '../../types/ws.types'
import {EGameMode, IEndPositionsState} from './atoms'

const audioManager = new AudioManager()

const FOLLOW_CAM_DISTANCE = 60
const FOLLOW_CAM_DISTANCE_MAX_MULTIPLIER = 1.7
const FOLLOW_CAM_LOOKAT_LEAD = 20
const FOLLOW_CAM_Y_POS = 20
const FOLLOW_CAM_Y_POS_MAX_MULTIPLIER = 1.6

export const FINISH_LINE_X_OFFSET = 40
export const FINISH_LINE_X_OFFSET_NEG = 20 // used to play finish line sound

class FrameStore {
  private static _instance: FrameStore | null = null

  #InitialCameraPos = new Vector3(-200, 60, 0)
  #PathStartVec: Vector3 = new Vector3(0, 0, 0)
  #PathEndVec: Vector3 = new Vector3(0, 0, 0)
  #PathEndOffsetVec: Vector3 = new Vector3(0, 0, 0)
  #FollowCamVec: Vector3 = new Vector3(0, FOLLOW_CAM_Y_POS, 0)
  #FollowCamLookatVec: Vector3 = new Vector3(0, 0, 0)
  #SlotVectors: (Vector3 | null)[] = [null, null, null, null, null]
  #FirstPlaceTeamSpeed: number = 0
  #FirstPlaceTeamPercentage: number = 0
  #FirstPlaceTeamVec: Vector3 = new Vector3(0, 0, 0)
  #LastPlaceTeamVec: Vector3 = new Vector3(0, 0, 0)
  #PreviousFurthestX: number | null = null

  #ServerTimeDiff: number = 0
  #Leaderboard?: ILeaderboardMessage
  #EndPositions: IEndPositionsState[] = []
  #ShouldSoftEndGame = false
  #GameMode: EGameMode = EGameMode.NONE

  constructor() {
    if (FrameStore._instance) {
      return FrameStore._instance
    }
    FrameStore._instance = this
  }

  reset() {
    this.#FollowCamVec = new Vector3(0, FOLLOW_CAM_Y_POS, 0)
    this.#FollowCamLookatVec = new Vector3(0, 0, 0)
    this.#FirstPlaceTeamSpeed = 0
    this.#FirstPlaceTeamPercentage = 0
    this.#FirstPlaceTeamVec = new Vector3(0, 0, 0)
    this.#LastPlaceTeamVec = new Vector3(0, 0, 0)
    this.#PreviousFurthestX = this.#PathStartVec.x

    this.#ServerTimeDiff = 0
    this.#Leaderboard = undefined
    this.#EndPositions = []
    this.#ShouldSoftEndGame = false

    this.#GameMode = EGameMode.NONE

    this.setTeamSlotVec(1, null)
    this.setTeamSlotVec(2, null)
    this.setTeamSlotVec(3, null)
    this.setTeamSlotVec(4, null)
    this.setTeamSlotVec(5, null)

    FrameStore._instance = null

    // these shouldnt have to be reset
    // this.#InitialCameraPos = new Vector3(-200, 60, 0)
    // this.#PathStartVec = new Vector3(0, 0, 0)
    // this.#PathEndVec = new Vector3(0, 0, 0)
  }

  /**
   * Info
   */
  setInfo({serverTime}: IInfoMessage) {
    const localTime = Date.now()
    this.#ServerTimeDiff = localTime - serverTime
  }

  /**
   * Leaderboard
   */
  setLeaderboard(leaderboardData: ILeaderboardMessage) {
    this.#Leaderboard = leaderboardData
  }

  getLeaderboard() {
    return this.#Leaderboard
  }

  /**
   * Path
   */
  setPathInfo(pathStart: Vector3, pathEnd: Vector3) {
    this.#PathStartVec = pathStart
    this.#PathEndVec = pathEnd
    this.#PathEndOffsetVec = new Vector3(pathEnd.x + FINISH_LINE_X_OFFSET, pathEnd.y, pathEnd.z)

    this.#PreviousFurthestX = pathStart.x
  }

  getPathEndVec() {
    return this.#PathEndVec
  }

  /**
   * Used to allow the player to move a bit further than the finish line
   */
  getPathEndOffsetVec() {
    return this.#PathEndOffsetVec
  }

  /**
   * Camera
   */
  getInitialCameraPos() {
    return this.#InitialCameraPos
  }

  getFollowCamVec() {
    return this.#FollowCamVec
  }

  setFollowCamVec(vec: Vector3) {
    this.#FollowCamVec = vec
  }

  getFollowCamLookatVec() {
    return this.#FollowCamLookatVec
  }

  setFollowCamLookatVec(vec: Vector3) {
    this.#FollowCamLookatVec = vec
  }

  /**
   * Get server diff time
   */
  getServerTimeDiff() {
    return this.#ServerTimeDiff
  }

  /**
   * Game mode
   */
  setGameMode(mode: EGameMode) {
    this.#GameMode = mode
  }

  /**
   * Update camera vectors
   * This is called every frame from the GameCamera component
   */
  updateCameraVectors() {
    const furthestVec = this.#FirstPlaceTeamVec

    const _FOLLOW_CAM_DISTANCE =
      this.#GameMode === EGameMode.SINGLE_PLAYER ? FOLLOW_CAM_DISTANCE / 2 : FOLLOW_CAM_DISTANCE

    const _FOLLOW_CAM_Y_POS =
      this.#GameMode === EGameMode.SINGLE_PLAYER ? FOLLOW_CAM_Y_POS / 2 : FOLLOW_CAM_Y_POS

    const _FOLLOW_CAM_LOOKAT_LEAD =
      this.#GameMode === EGameMode.SINGLE_PLAYER
        ? FOLLOW_CAM_LOOKAT_LEAD / 2
        : FOLLOW_CAM_LOOKAT_LEAD

    // calculate follow cam distance and height
    const calculatedFollowCamDistance =
      _FOLLOW_CAM_DISTANCE *
      Math.min(1 + this.#FirstPlaceTeamSpeed, FOLLOW_CAM_DISTANCE_MAX_MULTIPLIER)

    const calculatedFollowCamHeight =
      _FOLLOW_CAM_Y_POS * Math.min(1 + this.#FirstPlaceTeamSpeed, FOLLOW_CAM_Y_POS_MAX_MULTIPLIER)

    const calculatedFollowCamLookatDistance =
      _FOLLOW_CAM_LOOKAT_LEAD / (1 + this.#FirstPlaceTeamSpeed)

    if (furthestVec) {
      // update the follow cam vector
      this.setFollowCamVec(
        new Vector3(furthestVec.x - calculatedFollowCamDistance, calculatedFollowCamHeight, 0),
      )

      // update the follow cam lookat vector
      this.setFollowCamLookatVec(
        new Vector3(furthestVec.x + calculatedFollowCamLookatDistance, 0, furthestVec.z),
      )
    } else {
      this.setFollowCamVec(
        new Vector3(this.#PathStartVec.x - _FOLLOW_CAM_DISTANCE, _FOLLOW_CAM_Y_POS, 0),
      )
      this.setFollowCamLookatVec(new Vector3(this.#PathStartVec.x + FOLLOW_CAM_LOOKAT_LEAD, 0, 0))
    }

    // audio
    if (this.#FirstPlaceTeamPercentage < 0.95) {
      audioManager.SFX.cowbells_cheering?.volume(this.#FirstPlaceTeamPercentage)
      audioManager.SFX.crowdcheering?.volume(this.#FirstPlaceTeamPercentage)
      audioManager.BGM.ingame?.volume(Math.min(1 - this.#FirstPlaceTeamPercentage, 0.5))
    }
  }

  /**
   * Update first and last team place vectors
   * and calculate the first place team speed
   * This is called every frame from the GameCamera component
   */
  updateFirstAndLastTeamPlace() {
    let furthestVec: Vector3 = this.#PathStartVec // start with furthest distance
    let shortestVec: Vector3 = this.#PathEndVec // start with furthest distance

    // loop through all slot vectors to find the furthest and shortest
    for (let i = 0; i < this.#SlotVectors.length; i++) {
      const slotVec = this.#SlotVectors[i]

      // furthest
      if (
        slotVec &&
        slotVec.distanceTo(this.#PathEndVec) < furthestVec.distanceTo(this.#PathEndVec)
      ) {
        furthestVec = slotVec
      }

      // shortest
      if (
        slotVec &&
        slotVec.distanceTo(this.#PathStartVec) < shortestVec.distanceTo(this.#PathStartVec)
      ) {
        shortestVec = slotVec
      }
    }

    // calculate furthest team speed
    if (furthestVec.x) {
      if (!this.#PreviousFurthestX) {
        this.#PreviousFurthestX = furthestVec.x
      }
      const diff = Math.abs(furthestVec.x - this.#PreviousFurthestX)
      const diffRounded = Math.round(diff * 100) / 100
      this.#FirstPlaceTeamSpeed = diffRounded ? Math.round(diffRounded * 100) / 100 : 0
      this.#PreviousFurthestX = furthestVec.x
    }

    // set first place team vectors
    if (furthestVec.x === this.#PathStartVec.x) {
      this.#FirstPlaceTeamVec = this.#PathStartVec
    } else {
      this.#FirstPlaceTeamVec = furthestVec
    }

    // set last place team vectors
    if (shortestVec.x === this.#PathEndVec.x) {
      this.#LastPlaceTeamVec = this.#PathEndVec
    } else {
      this.#LastPlaceTeamVec = shortestVec
    }

    // update first team percentage
    // this is used for audio volume when the first team is close to the end
    this.#FirstPlaceTeamPercentage = clamp(
      inverseLerp(this.#PathStartVec.x, this.#PathEndVec.x, furthestVec.x),
      0,
      1,
    )
  }

  getFirstPlaceTeamVec() {
    return this.#FirstPlaceTeamVec
  }

  getLastPlaceTeamVec() {
    return this.#LastPlaceTeamVec
  }

  getFirstPlaceTeamSpeed() {
    return this.#FirstPlaceTeamSpeed
  }

  /**
   * Slot vectors
   * This is called every frame from the Player component to update the slot vectors
   */
  setTeamSlotVec(slot: number, vec: Vector3 | null) {
    this.#SlotVectors[slot - 1] = vec
  }

  /**
   * End states
   * This is called every frame from the Player component to check if the player has crossed the finish line
   */
  setShouldSoftEnd() {
    this.#ShouldSoftEndGame = true
  }
  getShouldSoftEnd() {
    return this.#ShouldSoftEndGame
  }

  /**
   * End positions
   */
  appendEndPositions(endPositionData: IEndPositionsState) {
    this.#EndPositions.push(endPositionData)
  }
  getSlotEndPosition(slot: number) {
    return this.#EndPositions.find(endPos => endPos.slot === slot)
  }
}

export default FrameStore
